import { useTranslation } from 'react-i18next';
import { MerchantInfo, MerchantStatus } from 'services/merchants/types';

import { FormLayout } from 'components/form/formLayout/formLayout';
import { FormFieldDescription } from 'components/form/formLayout/types';
import { IconError } from 'components/icons/error';
import { Input, InputType } from 'components/inputs';

import { SetModalProps } from 'pages/merchant/route';

import { getValidationErrorMessage } from 'utils/error';

import { useAutoKybFlowContext } from '../../kyb/AutoKybFlowContext';
import { FORM_FIELDS, LABELS } from '../keys';
import { ActionButton, Adornment, ButtonsRow, KybErrorLabel } from './styles';
import { formatKycStatus } from './utils';

interface GeneralAccountSettingFormProps extends SetModalProps {
  merchant: MerchantInfo;
  onFieldValueChange: (key: string, value: any) => void;
  onStatusChange: (status: MerchantStatus) => void;
  hiddenFields: Record<string, boolean>;
  isUpdating: boolean;
  isUpdatingStatus: boolean;
  error?: Record<string, string>;
  readOnlyMode: boolean;
  onOpenKyb: () => Promise<void>;
  onOpenAssociatedPerson: () => void;
}

export const GeneralAccountSettingForm = ({
  merchant,
  onFieldValueChange,
  onStatusChange,
  hiddenFields,
  isUpdating,
  isUpdatingStatus,
  error,
  readOnlyMode,
  onOpenKyb,
  onOpenAssociatedPerson,
}: GeneralAccountSettingFormProps) => {
  const { t } = useTranslation();
  const { isProcessing } = useAutoKybFlowContext();

  const handleChange = (fieldName: string) => (value: any) => {
    onFieldValueChange(fieldName, value);
  };

  const fields: FormFieldDescription[] = [
    {
      key: FORM_FIELDS.ACCOUNT_ID,
      label: t(LABELS.FORM_FIELDS.ACCOUNT_ID),
      hidden: hiddenFields[FORM_FIELDS.ACCOUNT_ID],
      renderInput: () => merchant.uuid,
    },
    {
      key: FORM_FIELDS.ACCOUNT_NAME,
      label: t(LABELS.FORM_FIELDS.ACCOUNT_NAME),
      hidden: hiddenFields[FORM_FIELDS.ACCOUNT_NAME],
      renderInput: ({ readonly }) =>
        readonly ? (
          merchant.name
        ) : (
          <Input
            fullWidth
            value={merchant.name}
            onChange={handleChange(FORM_FIELDS.ACCOUNT_NAME)}
            placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_NAME)}
            disabled={isUpdating}
          />
        ),
    },
    {
      key: FORM_FIELDS.MAIN_FIAT_CURRENCY,
      label: t(LABELS.FORM_FIELDS.MAIN_FIAT_CURRENCY),
      hidden: hiddenFields[FORM_FIELDS.MAIN_FIAT_CURRENCY],
      renderInput: () => merchant.mainFiatCurrency || !merchant.stablecoinSettlementEnabled,
    },
    {
      key: FORM_FIELDS.GROUP_ACCOUNT,
      label: t(LABELS.FORM_FIELDS.GROUP_ACCOUNT),
      hidden: !merchant.groupAccount || hiddenFields[FORM_FIELDS.GROUP_ACCOUNT] || !merchant.pwcEnabled,
      renderInput: () => merchant.groupAccount?.name,
    },
    {
      key: FORM_FIELDS.MERCHANT_ID,
      label: t(LABELS.FORM_FIELDS.MERCHANT_ID),
      hidden: hiddenFields[FORM_FIELDS.MERCHANT_ID],
      renderInput: ({ readonly }) =>
        readonly ? (
          merchant.shift4MerchantId
        ) : (
          <Input
            fullWidth
            value={merchant.shift4MerchantId ?? ''}
            onChange={handleChange(FORM_FIELDS.MERCHANT_ID)}
            placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS.MERCHANT_ID)}
            disabled={isUpdating}
          />
        ),
    },
    {
      key: FORM_FIELDS.BRIDGE_CUSTOMER_ID,
      label: t(LABELS.FORM_FIELDS.BRIDGE_CUSTOMER_ID),
      hidden: hiddenFields[FORM_FIELDS.BRIDGE_CUSTOMER_ID],
      renderInput: () => merchant.bridgeCustomerId,
    },
    {
      key: FORM_FIELDS.MESH_SUBCLIENT_ID,
      label: t(LABELS.FORM_FIELDS.MESH_SUBCLIENT_ID),
      hidden: hiddenFields[FORM_FIELDS.MESH_SUBCLIENT_ID],
      renderInput: ({ readonly }) =>
        readonly ? (
          merchant.meshSubclientId
        ) : (
          <Input
            fullWidth
            value={merchant.meshSubclientId ?? ''}
            onChange={handleChange(FORM_FIELDS.MESH_SUBCLIENT_ID)}
            placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS.MESH_SUBCLIENT_ID)}
            disabled={isUpdating}
          />
        ),
    },
    {
      key: FORM_FIELDS.WEBHOOK_URL,
      hidden: hiddenFields[FORM_FIELDS.WEBHOOK_URL],
      label: t(LABELS.FORM_FIELDS.WEBHOOK_URL),
      renderInput: ({ readonly }) =>
        readonly ? (
          `${LABELS.ADORNMENTS.HTTPS}${merchant.webhookUrl}`
        ) : (
          <Input
            fullWidth
            value={merchant.webhookUrl ?? ''}
            onChange={handleChange(FORM_FIELDS.WEBHOOK_URL)}
            startAdornment={<Adornment>{LABELS.ADORNMENTS.HTTPS}</Adornment>}
            disabled={isUpdating}
          />
        ),
    },
    {
      key: FORM_FIELDS.PROCESSING_FEE,
      label: t(LABELS.FORM_FIELDS.PROCESSING_FEE),
      hidden: hiddenFields[FORM_FIELDS.PROCESSING_FEE],
      renderInput: ({ readonly }) =>
        readonly ? (
          t(LABELS.FORM_FIELDS.VALUES.PROCESSING_FEE, { fee: merchant.processingFee })
        ) : (
          <Input
            fullWidth
            value={merchant.processingFee?.toString()}
            onChange={handleChange(FORM_FIELDS.PROCESSING_FEE)}
            placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS.PROCESSING_FEE)}
            type={InputType.Number}
            endAdornment={<Adornment>{LABELS.ADORNMENTS.PERCENT}</Adornment>}
            disabled={isUpdating}
          />
        ),
    },
    {
      key: FORM_FIELDS.STATUS,
      label: t(LABELS.FORM_FIELDS.STATUS),
      hidden: hiddenFields[FORM_FIELDS.STATUS],
      renderInput: () =>
        ({
          [MerchantStatus.Active]: t(LABELS.FORM_FIELDS.VALUES.MERCHANT_STATUS_ACTIVE),
          [MerchantStatus.Onboarding]: t(LABELS.FORM_FIELDS.VALUES.MERCHANT_STATUS_ONBOARDING),
          [MerchantStatus.Deactivated]: t(LABELS.FORM_FIELDS.VALUES.MERCHANT_STATUS_DEACTIVATED),
          [MerchantStatus.Inactive]: t(LABELS.FORM_FIELDS.VALUES.MERCHANT_STATUS_INACTIVE),
        })[merchant.status],
    },
    {
      key: FORM_FIELDS.KYC_STATUS,
      label: t(LABELS.FORM_FIELDS.KYC_STATUS),
      hidden: hiddenFields[FORM_FIELDS.KYC_STATUS],
      renderInput: () => formatKycStatus(merchant.kyc?.kycStatus),
    },
    {
      key: FORM_FIELDS.KYB_ERROR,
      label: (
        <KybErrorLabel>
          <IconError />
          {t(LABELS.FORM_FIELDS.KYB_ERROR)}
        </KybErrorLabel>
      ),
      hidden: hiddenFields[FORM_FIELDS.KYB_ERROR] || !merchant.kyc?.autoKycErrors,
      renderInput: () => getValidationErrorMessage(merchant.kyc?.autoKycErrors),
    },
    {
      key: FORM_FIELDS.CHANGE_STATUS,
      label: t(LABELS.FORM_FIELDS.CHANGE_STATUS),
      hidden: hiddenFields[FORM_FIELDS.CHANGE_STATUS],
      renderInput: () => {
        if (merchant.status === MerchantStatus.Active) {
          return (
            <ActionButton
              disabled={isUpdating}
              isLoading={isUpdatingStatus}
              danger
              onClick={() => onStatusChange(MerchantStatus.Deactivated)}
            >
              {t(LABELS.FORM_FIELDS.ACTIONS.DEACTIVATE)}
            </ActionButton>
          );
        }
        return (
          <ActionButton disabled={isUpdating} isLoading={isUpdatingStatus} secondary onClick={() => onStatusChange(MerchantStatus.Active)}>
            {t(LABELS.FORM_FIELDS.ACTIONS.ACTIVATE)}
          </ActionButton>
        );
      },
    },
    {
      key: FORM_FIELDS.UPDATE_KYB_INFORMATION,
      label: t(LABELS.FORM_FIELDS.UPDATE_KYB_INFORMATION),
      hidden: hiddenFields[FORM_FIELDS.UPDATE_KYB_INFORMATION],
      renderInput: () => (
        <ButtonsRow>
          <ActionButton disabled={isUpdating || isProcessing} isLoading={isUpdating || isProcessing} secondary onClick={onOpenKyb}>
            {t(LABELS.FORM_FIELDS.ACTIONS.UPDATE_KYB)}
          </ActionButton>
          <ActionButton
            disabled={isUpdating || isProcessing}
            isLoading={isUpdating || isProcessing}
            secondary
            onClick={onOpenAssociatedPerson}
          >
            {t(LABELS.FORM_FIELDS.ACTIONS.UPDATE_UBO)}
          </ActionButton>
        </ButtonsRow>
      ),
    },
  ];

  return <FormLayout fields={fields} readonly={readOnlyMode} error={error} />;
};
